import Vue from 'vue';
import Moment from 'moment-timezone';
import XLSX from 'xlsx';
import { range, cloneDeep } from 'lodash';

import { i18n } from '@/plugins/i18n';
import { getActionWorkflowStep } from '@/utils/action-utils';
import { setColumnSize } from '@/utils/sheets';
import { decodeHTML, removeHTMLSpans, treeIdsText, nestedFlatten } from '@/utils/main';


/**
 * Check that a string is not empty
 * @param  {String}  str - string to check
 * @return {Boolean} - True if not empty
 */
function isStringGood(str) {
  return typeof str === 'string' && str.length > 0;
}

/**
 * Creates a causes excel sheet used when calling exportToXl
 * @param  {Array} causes - tree of problem causes
 * @return {Array} - array usable by XLSX
 */
function createCausesTab(causes, flatCauses) {
  let colIdx = -1;
  let rowIdx = 0;
  const rows = [[]];

  function appendCauses(c) {
    colIdx++;
    for (let i = 0, len = c.length; i < len; i++) {
      if (i > 0) {
        rows[++rowIdx] = [];
        for (let j = 0; j < colIdx; j++) {
          rows[rowIdx].push('');
        }
      }
      rows[rowIdx][colIdx] = c[i].text;
      appendCauses(c[i].wCauses);
    }
    colIdx--;
  }
  appendCauses(causes);
  rows.unshift([`${i18n.t('message.CAUSES')} (${flatCauses.length})`]);
  return rows;
}

/**
 * Creates an actions excel sheet used when calling exportToXl
 * @param  {Array} actions - array of action
 * @return {Array} - array usable by XLSX
 */
function createActionTab(actions, allUsers, flatCauses) {
  const tab = [
    [
      `${i18n.t('message.ACTIONS')} (${actions.length})`,
    ],
    [
      i18n.t('message.NAME'), i18n.t('message.TEAM'), i18n.t('message.PDCA'),
      i18n.t('message.IMPACT'), i18n.t('message.LEADER'), i18n.t('message.DEADLINE'),
      i18n.t('message.GAIN'), i18n.t('message.INDIC_UNIT'), i18n.t('message.CAUSE'),
    ],
  ];
  for (let idx = 0, len = actions.length; idx < len; idx++) {
    const action = actions[idx];
    const cause = flatCauses.find(({ id }) => action.cause === id);
    const causeText = cause ? cause.text : '';
    const user = allUsers.find(u => u.id === action.leader);
    tab.push([
      decodeHTML(action.name),
      decodeHTML(action.teamName),
      getActionWorkflowStep(action).text,
      action.impact,
      user ? decodeHTML(user.label) : '',
      action.deadline ? action.deadline.split(' ')[0].replace(/-/g, '/') : '',
      action.gain,
      action.gainUnit,
      causeText,
    ]);
  }
  return tab;
}

/**
 * Return a score out of 100 according to a problem's prams
 * @param  {Object} pb - problem object
 * @return {Number} - score between 0 and 100
 */
export function getProblemTrustIndex(pb) {
  const scoreKey = [
    { key: 'files', weight: 20, isGood: data => data instanceof Array && data.length },
    { key: 'lWhat', weight: 15, isGood: data => isStringGood(data) },
    { key: 'lWhy', weight: 15, isGood: data => isStringGood(data) },
    { key: 'lWho', weight: 9, isGood: data => isStringGood(data) },
    { key: 'lWhen', weight: 9, isGood: data => isStringGood(data) },
    { key: 'lWhere', weight: 9, isGood: data => isStringGood(data) },
    { key: 'lHow', weight: 9, isGood: data => isStringGood(data) },
    { key: 'lHowMuch', weight: 9, isGood: data => isStringGood(data) },
    { key: 'types', weight: 5, isGood: data => data instanceof Array && data.length },
  ];
  return scoreKey.reduce((score, crit) => (score + (crit.isGood(pb[crit.key]) ? crit.weight : 0)), 0);
}

/**
 * Create an excel sheet for a list of problems
 * @param  {Array} problems    List of problems
 */
export function exportListToExcel(problems) {
  const dateFormat = i18n.t('dateFormat');
  const no = i18n.t('message.NO');
  const yes = i18n.t('message.YES');
  const pbColumns = [
    i18n.t('message.TEAM'),
    i18n.t('message.LEADER'),
    i18n.t('message.PROBLEM'),
    i18n.t('message.WHAT'),
    i18n.t('message.WHO'),
    i18n.t('message.WHERE'),
    i18n.t('message.WHEN'),
    i18n.t('message.HOW'),
    i18n.t('message.HOW_MUCH'),
    i18n.t('message.WHY'),
    i18n.t('message.IMPACT'),
    i18n.t('message.DEADLINE'),
    i18n.t('message.ORIGINAL_DEADLINE'),
    i18n.t('message.INDICATORS'),
    i18n.t('message.LOCATION'),
    i18n.t('message.TYPE'),
    i18n.t('message.STATE'),
    i18n.t('message.CREATION_DATE'),
    i18n.t('message.LAST_MODIFIED_DATE'),
    i18n.t('message.ANALYSIS_STATE_DATE'),
    i18n.t('message.FIRST_ACTION_WORK_DATE'),
    i18n.t('message.ARCHIVE_DATE'),
    i18n.t('message.ARCHIVED_THIS_WEEK'),
    i18n.t('message.COMMENTS'),
    i18n.t('message.GW'),
    i18n.t('message.GW_STEP'),
  ];
  const causeColumns = [
    i18n.t('message.PROBLEM'),
    i18n.t('message.DESCRIPTION'),
    i18n.t('message.CAUSE'),
  ];
  const actionColumns = [
    i18n.t('message.PROBLEM'),
    i18n.t('message.DESCRIPTION'),
    i18n.t('message.ACTION'),
    i18n.t('message.TEAM'),
    i18n.t('message.PDCA'),
    i18n.t('message.IMPACT'),
    i18n.t('message.LEADER'),
    i18n.t('message.DEADLINE'),
    i18n.t('message.CAUSE'),
  ];
  const pbRows = [pbColumns];
  const causeRows = [causeColumns];
  const actionRows = [actionColumns];
  const now = Vue.moment();

  function addChildCauseDetails(rows, currentRow, cause, pbCauses) {
    const childCauses = pbCauses.filter(otherPbCause => otherPbCause.cause === cause.id);

    if (childCauses.length > 0) {
      childCauses.forEach((childCause) => {
        const row = cloneDeep(currentRow);
        row.push(childCause.text);
        addChildCauseDetails(rows, row, childCause, pbCauses);
      });
    } else {
      rows.push(currentRow);
    }
  }

  function getCellMerges(rows, indexMaxToMerge = 10) {
    const merges = [];
    let currentMerge = {};
    for (let c = 0; c < indexMaxToMerge; c++) {
      for (let r = 1; r < rows.length; r++) {
        if (rows[r][c] && rows[r + 1] && rows[r + 1][c] === rows[r][c]) {
          if (!currentMerge.s) {
            currentMerge.s = { r, c };
          }
        } else if (currentMerge.s) {
          currentMerge.e = { r, c };
          merges.push(currentMerge);
          currentMerge = {};
        }
      }
    }
    return merges;
  }

  problems.forEach((pb) => {
    // Beginning of the analysis : Minimum between first action date and first cause date
    pb.startAnalysisDate = '';
    if (pb.firstCauseDate || pb.firstActionDate) {
      const firstCause = Vue.moment(pb.firstCauseDate);
      const firstAction = Vue.moment(pb.firstActionDate);
      if (
        (firstCause.isValid() && !firstAction.isValid())
        || (firstCause.isValid() && firstCause.isBefore(firstAction))
      ) {
        pb.startAnalysisDate = firstCause.format(dateFormat);
      } else {
        pb.startAnalysisDate = firstAction.format(dateFormat);
      }
    }
    // For older problems (before sept 2020), deadline was set by default. So if the deadline hasn't changed,
    // there's no history record.
    const deadline = Vue.moment(pb.deadline);
    const originalDeadline = Vue.moment(pb.originalDeadline);
    const deadlineVal = deadline.isValid() ? deadline.format(dateFormat) : '';
    // Archiving date
    let archivedDateCol = '';
    let archivedThisWeek = no;
    if (pb.archivedOn) {
      const archiveDate = Vue.moment(pb.archivedOn);
      archivedDateCol = archiveDate.isValid() ? archiveDate.format(dateFormat) : '';
      // Yes/No column to indicate whether this problem was archived in the last 7 days
      archivedThisWeek = archiveDate.isValid() && now.diff(archiveDate, 'd') <= 7 ? yes : no;
    }

    let firstActionStartDate = '';
    if (pb.firstActionStartDate) {
      const mDate = Vue.moment(pb.firstActionStartDate);
      firstActionStartDate = mDate.isValid() ? mDate.format(dateFormat) : '';
    }
    // Concatenate comments in a column
    const comments = pb.comments
      .map((comment) => {
        const fDate = Vue.moment(comment.created).format(dateFormat);
        return `${fDate}${i18n.t('message.TWO_DOTS')}${comment.teamName}`
          + `${i18n.t('message.TWO_DOTS')}${comment.text}`;
      })
      .join('\n');
    pbRows.push([
      pb.teamName,
      i18n.t('fullNameFormat', pb.leaders[0] || {}),
      pb.id,
      pb.lWhat,
      removeHTMLSpans(pb.lWho),
      removeHTMLSpans(pb.lWhere),
      pb.lWhen,
      pb.lHow,
      pb.lHowMuch,
      pb.lWhy,
      pb.impact,
      deadlineVal,
      originalDeadline.isValid() ? originalDeadline.format(dateFormat) : deadlineVal,
      pb.indicators,
      pb.zones.map(z => z.name).join(', '),
      pb.types.map(t => t.name).join(', '),
      i18n.t('message.PB_STATES')[pb.state - 1],
      Vue.moment(pb.created).format(dateFormat),
      Vue.moment(pb.updated).format(dateFormat),
      pb.startAnalysisDate,
      firstActionStartDate,
      archivedDateCol,
      archivedThisWeek,
      comments,
      pb.gwName,
      pb.gwStepName,
    ]);

    pb.causes.forEach((cause) => {
      if (cause.cause === 0) {
        const currentRow = [
          pb.id,
          pb.lWhat,
          cause.text,
        ];
        addChildCauseDetails(causeRows, currentRow, cause, pb.causes);
      }
    });

    pb.actions.forEach((action) => {
      actionRows.push([
        pb.id,
        pb.lWhat,
        action.name,
        action.reportTeamName,
        getActionWorkflowStep(action).text,
        action.impact,
        i18n.t('fullNameFormat', { firstName: action.firstName, lastName: action.lastName }),
        action.deadline,
        action.causeText,
      ]);
    });
  });
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: i18n.t('message.PROBLEMS'),
    Subject: i18n.t('message.PROBLEMS'),
    CreateDate: Vue.moment().format(dateFormat),
  };

  const pbsSheet = XLSX.utils.aoa_to_sheet(pbRows);
  setColumnSize(pbsSheet, 30);
  XLSX.utils.book_append_sheet(wb, pbsSheet, i18n.t('message.PROBLEMS'));

  const causesSheet = XLSX.utils.aoa_to_sheet(causeRows);
  causesSheet['!merges'] = getCellMerges(causeRows);
  setColumnSize(causesSheet, 30);
  XLSX.utils.book_append_sheet(wb, causesSheet, i18n.t('message.CAUSES'));

  const actionsSheet = XLSX.utils.aoa_to_sheet(actionRows);
  actionsSheet['!merges'] = getCellMerges(actionRows, 2);
  setColumnSize(actionsSheet, 30);
  XLSX.utils.book_append_sheet(wb, actionsSheet, i18n.t('message.ACTIONS'));

  XLSX.writeFile(wb, `${i18n.t('message.PROBLEMS')}_Shizen_${Vue.moment().format(i18n.t('lFullDateFormat'))}.xlsx`);
}

/**
 * Create an excel sheet for a problem, its causes and actions
 */
export function exportToExcel(prblm, allIndicators, allZones, allTypes, allUsers) {
  const zoneLabel = treeIdsText(allZones, prblm.zones, 'name', 'zones');
  const typeLabel = treeIdsText(allTypes, prblm.types, 'name', 'types');
  const formatDeadline = Moment(prblm.deadline).format(i18n.t('dateFormat'));
  const formatCreationDate = Moment(prblm.created).format(i18n.t('dateFormat'));
  const problemData = [
    [i18n.t('message.PROBLEM')],
    [i18n.t('message.WHAT'), prblm.lWhat],
    [i18n.t('message.WHO'), removeHTMLSpans(prblm.lWho)],
    [i18n.t('message.WHERE'), removeHTMLSpans(prblm.lWhere)],
    [i18n.t('message.WHEN'), prblm.lWhen],
    [i18n.t('message.HOW'), prblm.lHow],
    [i18n.t('message.HOW_MUCH'), prblm.lHowMuch],
    [i18n.t('message.WHY'), prblm.lWhy],
    [i18n.t('message.IMPACT'), prblm.impact],
    [i18n.t('message.DEADLINE'), formatDeadline],
    [i18n.t(
      'message.INDICATORS',
    ),
    allIndicators.filter(({ id }) => prblm.indicators.includes(id)).map(
      ({ name }) => name,
    ).join(', ')],
    [i18n.t('message.LOCATION'), zoneLabel],
    [i18n.t('message.TYPE'), typeLabel],
    [i18n.t('message.CREATION_DATE'), formatCreationDate],
  ];
  const flatCauses = nestedFlatten(prblm.wCauses, 'wCauses', 'id');
  // Creates action tab
  const actionData = createActionTab(prblm.actions, allUsers, flatCauses);
  // Creates cause data
  const causesData = createCausesTab(prblm.wCauses, flatCauses);
  // Get current date
  const today = Moment();
  // Creates the new work book
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: prblm.lWhat,
    Subject: prblm.lWhat,
    CreateDate: today.format(i18n.t('dateFormat')),
  };
  // Creates the excel file;
  const summarySheet = XLSX.utils.aoa_to_sheet(problemData);
  XLSX.utils.sheet_add_aoa(summarySheet, causesData, { origin: { r: problemData.length + 2, c: 0 } });
  XLSX.utils.sheet_add_aoa(summarySheet, actionData, {
    origin: { r: problemData.length + causesData.length + 4, c: 0 },
  });
  setColumnSize(summarySheet, 30);
  XLSX.utils.book_append_sheet(wb, summarySheet, i18n.t('message.SUMMARY'));

  const problemSheet = XLSX.utils.aoa_to_sheet(problemData);
  setColumnSize(problemSheet, 30);
  XLSX.utils.book_append_sheet(wb, problemSheet, i18n.t('message.PROBLEM'));

  const causeSheet = XLSX.utils.aoa_to_sheet(causesData);
  setColumnSize(causeSheet, 30);
  XLSX.utils.book_append_sheet(wb, causeSheet, i18n.t('message.CAUSES'));

  const actionSheet = XLSX.utils.aoa_to_sheet(actionData);
  setColumnSize(actionSheet, 30);
  XLSX.utils.book_append_sheet(wb, actionSheet, i18n.t('message.ACTIONS'));
  XLSX.writeFile(wb, `${i18n.t('message.PROBLEM')}${prblm.created.split(' ')[0]}.xlsx`);
}

/**
 * Return the CSS class of a securing icon for problems and actions.
 * @param  {Object} [problem]       If set return the class according to the 'secuState' property of the problem
 * @param  {Object} [action=null]   If problem not set and action is set, return class
 *                                  according to the 'state' property of the action
 * @return {String}                 CSS class
 */
export function securingIconClass(problem, action = null) {
  if (problem !== null) {
    switch (problem.secuState) {
      case 4: return 'securing-icon securing-icon--green';
      default: return 'securing-icon';
    }
  } else if (action !== null) {
    switch (action.state) {
      case 2: return 'securing-icon securing-icon--green';
      default: return 'securing-icon';
    }
  }
  return '';
}


/**
 * Output the XHR parameter for filtering problems/actions with the ImpactSelector value
 * @param  {Array} impactFilter  ImpactSelector value
 * @return {Object}              Param to pass through the XHR
 */
export function parseImpactFilterParam(impactFilter) {
  if (impactFilter instanceof Array) {
    const filter = impactFilter.slice().sort();
    // Before Shizen v2, impact could be a decimal number
    return {
      min: filter[0] - 0.5,
      max: filter[filter.length - 1] + 0.5,
    };
  }
  return impactFilter;
}

/**
 * Convert the old impact filter parameters to the new array format
 * @param  {Object} impactFilter  Old format of the impact params : object with a min and amx values
 * @return {Array}               New format: Array containing each desired value
 */
export function convertImpactFilterToArray(impactFilter) {
  if (!(impactFilter instanceof Array)) {
    // Prior to Shizen v2, impactFilter was formatted as { min: max: }
    // Convert it to an array of values
    const min = Math.round(impactFilter.min || 0);
    const max = Math.round(impactFilter.max || 3);
    return range(min, max + 1);
  }
  return impactFilter;
}

export default {
  convertImpactFilterToArray,
  getProblemTrustIndex,
  exportListToExcel,
  exportToExcel,
  parseImpactFilterParam,
};
